<script setup lang="ts">
import type { ChartSeries } from './ocgChartRenderer';

import type { OcgCalculationProgress, OcgCurvesResult } from '@/src/store/calculators/OCGCalculatorStore';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { useOCGCalculatorStore } from '@/src/store/calculators/OCGCalculatorStore';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { renderOcgChart } from './ocgChartRenderer';

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    viewerID?: string;
    curvesResult?: OcgCurvesResult | null;
    isCalculating?: boolean;
    progress?: OcgCalculationProgress;
    error?: string | null;
  }>(),
  {
    viewerID: 'AnalysisGeneralView',
    curvesResult: null,
    isCalculating: false,
    progress: () => ({
      current: 0,
      total: 0,
      percent: 0,
      phaseIndex: 0,
      stage: '',
    }),
    error: null,
  },
);

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void;
  (e: 'recompute'): void;
}>();

const viewportStateStore = useViewportStateStore();
const ocgCalculatorStore = useOCGCalculatorStore();

const selectedImageIndices = computed(() => {
  if (props.viewerID) {
    return viewportStateStore.getSelectedImageIndices(props.viewerID);
  }
  return null;
});

const computedSeriesIndex = computed<number>(() => selectedImageIndices.value?.seriesIdx ?? 0);
const computedSliceIndex = computed<number>(() => selectedImageIndices.value?.sliceIdx ?? 0);
const computedImageIndex = computed<number>(() => selectedImageIndices.value?.imageIdx ?? 0);

const effectiveCurvesResult = computed<OcgCurvesResult | null>(() => {
  if (props.curvesResult) {
    return props.curvesResult;
  }
  return ocgCalculatorStore.getCurves(computedSeriesIndex.value, computedSliceIndex.value) || null;
});

const startPhase = computed<number>(() => effectiveCurvesResult.value?.startPhase ?? 0);

// Curve Display Options
export type OcgCurveDisplayMode = 'raw' | 'normalized' | 'mask-matched';

const curveDisplayMode = computed<OcgCurveDisplayMode>({
  get: () => ocgCalculatorStore.curveDisplayMode ?? (ocgCalculatorStore.isNormalized ? 'normalized' : 'raw'),
  set: (val: OcgCurveDisplayMode) => {
    ocgCalculatorStore.setCurveDisplayMode(val);
  },
});

const isNormalized = computed(() => curveDisplayMode.value === 'normalized');
const isSmoothed = ref(true);
const activeViewMode = ref<'territories' | 'segments'>('territories');
const showEdEsMarkers = ref(true);

const toggleEdEsMarkers = () => {
  showEdEsMarkers.value = !showEdEsMarkers.value;
  renderChart();
};

// Territory visibility
const visibleTerritories = ref({
  Global: true,
  LAD: true,
  RCA: true,
  LCx: true,
});

// Segment visibility
const visibleSegments = ref([true, true, true, true, true, true]);

// Territory Colors
const territoryColors: Record<string, string> = {
  Global: '#ff6700',
  LAD: '#ef00af',
  RCA: '#0066ff',
  LCx: '#00bb44',
};

// Hover tooltip position & state
const chartSvg = ref<SVGSVGElement | null>(null);
const hoveredPhase = ref<number | null>(null);
const tooltipPosition = ref({ x: 0, y: 0 });
const showTooltip = ref(false);

// Zoom state (stores [minPhase, maxPhase] or null)
const zoomDomain = ref<[number, number] | null>(null);

const width = 370;
const height = 190;
const margin = { top: 15, right: 5, bottom: 35, left: 30 };

const toggleTerritory = (territory: keyof typeof visibleTerritories.value) => {
  visibleTerritories.value[territory] = !visibleTerritories.value[territory];
  renderChart();
};

const hasMatchingCurvesResult = computed(() => {
  const res = effectiveCurvesResult.value;
  if (!res || res.nPhases === 0) return false;
  if (
    res.seriesIndex !== undefined
    && computedSeriesIndex.value !== undefined
    && res.seriesIndex !== computedSeriesIndex.value
  ) {
    return false;
  }
  if (
    res.sliceIndex !== undefined
    && computedSliceIndex.value !== undefined
    && res.sliceIndex !== computedSliceIndex.value
  ) {
    return false;
  }
  return true;
});

const activeSeriesList = computed<ChartSeries[]>(() => {
  const res = effectiveCurvesResult.value;
  if (!res || res.nPhases === 0) return [];
  if (
    res.seriesIndex !== undefined
    && computedSeriesIndex.value !== undefined
    && res.seriesIndex !== computedSeriesIndex.value
  ) {
    return [];
  }
  if (
    res.sliceIndex !== undefined
    && computedSliceIndex.value !== undefined
    && res.sliceIndex !== computedSliceIndex.value
  ) {
    return [];
  }

  const list: ChartSeries[] = [];
  const mode = curveDisplayMode.value;

  const getTerritoryData = (
    raw: number[],
    smoothed: number[],
    normalized: number[],
    maskMatched?: number[],
  ): number[] => {
    if (mode === 'normalized') {
      return normalized;
    }
    if (mode === 'mask-matched') {
      if (maskMatched && maskMatched.length > 0) return maskMatched;
      if (res.edMyoVal && res.edMyoVal > 0) {
        return smoothed.map(v => (Number.isFinite(v) ? v / res.edMyoVal! : v));
      }
      return normalized;
    }
    // Raw mode
    return isSmoothed.value ? smoothed : raw;
  };

  if (activeViewMode.value === 'territories') {
    if (visibleTerritories.value.Global) {
      list.push({
        id: 'Global',
        name: 'Global',
        color: territoryColors.Global,
        data: getTerritoryData(res.siGlobal, res.siGlobalSmoothed, res.siGlobalNormalized, res.siGlobalMaskMatched),
      });
    }
    if (visibleTerritories.value.LAD) {
      list.push({
        id: 'LAD',
        name: 'LAD',
        color: territoryColors.LAD,
        data: getTerritoryData(res.siLAD, res.siLADSmoothed, res.siLADNormalized, res.siLADMaskMatched),
      });
    }
    if (visibleTerritories.value.RCA) {
      list.push({
        id: 'RCA',
        name: 'RCA',
        color: territoryColors.RCA,
        data: getTerritoryData(res.siRCA, res.siRCASmoothed, res.siRCANormalized, res.siRCAMaskMatched),
      });
    }
    if (visibleTerritories.value.LCx) {
      list.push({
        id: 'LCx',
        name: 'LCx',
        color: territoryColors.LCx,
        data: getTerritoryData(res.siLCx, res.siLCxSmoothed, res.siLCxNormalized, res.siLCxMaskMatched),
      });
    }
  }
  // else if (activeViewMode.value === 'segments') {
  //   for (let s = 0; s < 6; s++) {
  //     if (visibleSegments.value[s]) {
  //       let segData = res.siAHA16.map(phRow => phRow[s] ?? 0);
  //       if (isNormalized.value && segData.length > 0) {
  //         const base = segData[0] || 1;
  //         segData = segData.map(v => (base !== 0 ? v / base : 1));
  //       }
  //       list.push({
  //         id: `S${s + 1}`,
  //         name: segmentNames[s],
  //         color: segmentColors[s],
  //         data: segData,
  //       });
  //     }
  //   }
  // }

  return list;
});

function renderChart() {
  if (props.isCalculating) return;
  const res = effectiveCurvesResult.value;
  if (!chartSvg.value || !res || activeSeriesList.value.length === 0) return;

  renderOcgChart({
    svgElement: chartSvg.value,
    series: activeSeriesList.value,
    nPhases: res.nPhases,
    startPhase: startPhase.value,
    esIndices: res.esIndices,
    edIndices: res.edIndices,
    excludedPhaseIndices: res.excludedPhaseIndices,
    currentPhaseIndex: computedImageIndex.value,
    showEdEsMarkers: showEdEsMarkers.value,
    width,
    height,
    margin,
    zoomDomain: zoomDomain.value,
    formatY: d => (curveDisplayMode.value === 'raw' ? Number(d).toFixed(0) : Number(d).toFixed(2)),
    onHover: (info) => {
      if (info) {
        hoveredPhase.value = info.phase;
        showTooltip.value = true;
        tooltipPosition.value = {
          x: Math.min(info.pixelX + 10, width - 150),
          y: Math.max(margin.top, 25),
        };
      }
      else {
        hoveredPhase.value = null;
        showTooltip.value = false;
      }
    },
    onZoomChange: (newDomain) => {
      zoomDomain.value = newDomain;
      renderChart();
    },
    onPhaseClick: (targetImageIdx) => {
      if (props.viewerID) {
        const targetSeriesIdx = res.seriesIndex ?? computedSeriesIndex.value;
        const targetSliceIdx = res.sliceIndex ?? computedSliceIndex.value;
        viewportStateStore.setSelectedImageIndices(props.viewerID, {
          seriesIdx: targetSeriesIdx,
          sliceIdx: targetSliceIdx,
          imageIdx: targetImageIdx,
        });
      }
    },
  });
}

// Reset zoom domain whenever slice or series selection changes
watch(
  [computedSeriesIndex, computedSliceIndex, effectiveCurvesResult],
  () => {
    zoomDomain.value = null;
  },
);

watch(
  [
    effectiveCurvesResult,
    computedSeriesIndex,
    computedSliceIndex,
    computedImageIndex,
    curveDisplayMode,
    isNormalized,
    isSmoothed,
    activeViewMode,
    visibleTerritories,
    visibleSegments,
    showEdEsMarkers,
    () => props.isOpen,
    zoomDomain,
  ],
  () => {
    if (props.isOpen && !props.isCalculating) {
      nextTick(() => renderChart());
    }
  },
  { deep: true },
);

watch(
  () => props.isCalculating,
  (calculating) => {
    if (calculating) {
      emit('update:isOpen', true);
    }
    else if (props.isOpen) {
      nextTick(() => renderChart());
    }
  },
);

onMounted(() => {
  if (props.isOpen && !props.isCalculating) {
    nextTick(() => renderChart());
  }
});

const togglePanel = () => {
  emit('update:isOpen', !props.isOpen);
};

const handleRecompute = () => {
  emit('update:isOpen', true);
  emit('recompute');
};
</script>

<template>
  <div class="ocg-curves-wrapper w-full flex flex-col gap-1.5 select-none">
    <!-- Header Control Bar (Always visible under thumbnails) -->
    <div class="flex items-center justify-between gap-2 px-1">
      <div
        class="header-toggle-group flex items-center gap-2 cursor-pointer select-none py-1 group"
        @click="togglePanel"
      >
        <i
          class="fa-solid fa-chevron-down text-slate-400 text-xs transition-transform duration-200 group-hover:text-white"
          :class="{ 'rotate-180': isOpen }"
        />
        <span class="text-xs font-semibold text-slate-200 group-hover:text-white transition-colors">
          {{ isOpen ? 'Hide OCG Curves' : 'View OCG Curves' }}
        </span>
      </div>

      <div class="flex items-center gap-1.5">
        <button
          v-if="isOpen"
          class="compute-curves-btn flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all shadow-sm cursor-pointer border"
          :class="showEdEsMarkers ? 'bg-slate-800 border-slate-600 text-cyan-300 hover:bg-slate-700' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'"
          :title="showEdEsMarkers ? 'Hide ED/ES phase indicators' : 'Show ED/ES phase indicators'"
          @click.stop="toggleEdEsMarkers"
        >
          <i class="fa-solid text-[10px]" :class="showEdEsMarkers ? 'fa-eye' : 'fa-eye-slash'" />
          <span>ED/ES</span>
        </button>

        <button
          v-if="!isCalculating && isOpen"
          class="compute-curves-btn flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-white transition-all shadow-sm cursor-pointer"
          :class="{ 'is-loading': isCalculating }"
          :disabled="isCalculating"
          title="Re-compute curves from active contours for current series and slice"
          @click.stop="handleRecompute"
        >
          <i class="fa-solid fa-bezier-curve text-[10px]" />
          <span>Compute Curves</span>
        </button>
      </div>
    </div>

    <span v-if="isOpen && hasMatchingCurvesResult" class="slice-indicator-badge flex items-center gap-2">
      <span>Showing Curves for Series {{ computedSeriesIndex + 1 }}, Slice {{ computedSliceIndex + 1 }}</span>
      <span class="text-[9px] opacity-75 font-mono" />
    </span>
    <!-- 3-Way Normalization Mode Toggle (Raw | Start-ED | Mask-Matched) -->
    <div class="flex items-center gap-2">
      <span class="text-xs font-semibold text-slate-200 group-hover:text-white transition-colors">Normalization:</span>
      <div v-if="isOpen && hasMatchingCurvesResult" class="toggle-group" title="Select curve normalization mode">
        <button
          class="toggle-btn"
          :class="{ active: curveDisplayMode === 'raw' }"
          title="Raw unnormalized signal intensity"
          @click.stop="curveDisplayMode = 'raw'"
        >
          Raw
        </button>
        <button
          class="toggle-btn"
          :class="{ active: curveDisplayMode === 'normalized' }"
          title="Normalized to Start-ED (each territory starts at 1.0)"
          @click.stop="curveDisplayMode = 'normalized'"
        >
          Start-ED
        </button>
        <button
          class="toggle-btn"
          :class="{ active: curveDisplayMode === 'mask-matched' }"
          title="Normalized by Global ED Myocardium median (matches mask overlay values)"
          @click.stop="curveDisplayMode = 'mask-matched'"
        >
          Mask-Matched
        </button>
      </div>
    </div>

    <!-- Expandable Curves Content Panel -->
    <div
      class="overflow-hidden transition-all duration-300"
      :class="isOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'"
    >
      <div class="flex flex-col gap-2.5">
        <!-- Error Banner -->
        <div v-if="error" class="p-2 rounded bg-red-500/20 border border-red-500/30 text-[11px] text-red-200 flex items-center gap-2">
          <i class="fa-solid fa-circle-exclamation text-red-400" />
          <span>{{ error }}</span>
        </div>

        <!-- Active Calculation Progress Bar -->
        <Transition name="fade">
          <div v-if="isCalculating" class="progress-container p-2.5 rounded-lg flex flex-col gap-1.5">
            <div class="flex items-center justify-between text-[11px] font-medium">
              <span class="flex items-center gap-1.5">
                <i class="fa-solid fa-spinner fa-spin text-xs text-cyan-400" />
                <span>Computing: {{ progress?.stage || 'Initializing...' }}</span>
              </span>
              <span class="font-mono text-xs text-white">{{ progress?.percent ?? 0 }}%</span>
            </div>

            <div class="w-full bg-slate-800 rounded-full h-2 overflow-hidden border border-white/10">
              <div
                class="progress-fill h-full rounded-full transition-all duration-150 ease-out"
                :style="{ width: `${Math.max(progress?.percent ? 5 : 2, progress?.percent ?? 0)}%` }"
              />
            </div>

            <div class="flex justify-between text-[10px] text-slate-400 font-mono">
              <span>Phases: {{ progress?.current ?? 0 }} / {{ progress?.total ?? 0 }}</span>
              <span>{{ (progress?.total ?? 0) - (progress?.current ?? 0) }} phases remaining</span>
            </div>
          </div>
        </Transition>

        <!-- No data state -->
        <div v-if="!hasMatchingCurvesResult && !isCalculating" class="py-4 text-center text-xs text-slate-400 flex flex-col items-center gap-2 p-3">
          <i class="fa-solid fa-wave-square text-2xl text-slate-600" />
          <span>No curves computed for Series {{ computedSeriesIndex + 1 }} Slice {{ computedSliceIndex + 1 }} yet.</span>
        </div>

        <!-- Curves Visualization (Renders only once data is complete and not calculating) -->
        <template v-else-if="hasMatchingCurvesResult && effectiveCurvesResult && !isCalculating">
          <!-- D3 SVG Line Chart -->
          <div class="relative w-full flex justify-center rounded-lg group">
            <svg
              ref="chartSvg"
              :viewBox="`0 0 ${width} ${height}`"
              class="w-full h-auto max-h-[200px]"
            />

            <!-- Tooltip Overlay -->
            <div
              v-if="showTooltip && hoveredPhase !== null"
              class="inline-chart-tooltip absolute p-1.5 rounded bg-[var(--md-surface-variant)] border border-white/20 text-[10px] pointer-events-none z-10 shadow-xl"
              :style="{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
              }"
            >
              <div class="font-bold text-white border-b border-white/10 pb-0.5 mb-1 flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <span>Phase {{ hoveredPhase + startPhase + 1 }}</span>
                  <span
                    v-if="effectiveCurvesResult.esIndices?.includes(hoveredPhase)"
                    class="px-1 py-0.2 text-[8px] font-bold rounded bg-amber-500/30 text-amber-300 border border-amber-500/40"
                  >
                    ES
                  </span>
                  <span
                    v-if="effectiveCurvesResult.edIndices?.includes(hoveredPhase)"
                    class="px-1 py-0.2 text-[8px] font-bold rounded bg-cyan-500/30 text-cyan-300 border border-cyan-500/40"
                  >
                    ED
                  </span>
                </div>
                <span v-if="effectiveCurvesResult.triggerTimes[hoveredPhase] !== undefined" class="text-slate-400 font-mono">
                  {{ effectiveCurvesResult.triggerTimes[hoveredPhase] }}ms
                </span>
              </div>
              <div class="flex flex-col gap-0.5">
                <div
                  v-for="s in activeSeriesList"
                  :key="s.id"
                  class="flex items-center justify-between gap-2"
                >
                  <span class="font-medium" :style="{ color: s.color }">{{ s.name }}:</span>
                  <span class="font-mono text-slate-200">
                    {{ Number.isFinite(s.data[hoveredPhase]) ? (curveDisplayMode === 'raw' ? s.data[hoveredPhase].toFixed(1) : s.data[hoveredPhase].toFixed(2)) : '-' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Zoom hint & Territory / Segment Filter Chips -->
          <div v-if="activeViewMode === 'territories'" class="flex flex-wrap items-center justify-between gap-1.5 px-4 -mt-4">
            <h6
              v-for="(color, name) in territoryColors"
              :key="name"
              class="flex items-center gap-1 transition-all cursor-pointer"
              :class="visibleTerritories[name as keyof typeof visibleTerritories] ? 'active-chip' : 'inactive-chip'"
              @click="toggleTerritory(name as keyof typeof visibleTerritories)"
            >
              <span class="w-8 h-0.5 rounded-full" :style="{ backgroundColor: color }" />
              <span class="text-[12px]">{{ name }}</span>
            </h6>
          </div>

          <!-- <div v-else-if="activeViewMode === 'segments'" class="flex flex-wrap items-center justify-between gap-1 -mt-4">
            <h6
              v-for="(name, sIdx) in segmentNames"
              :key="name"
              class="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium transition-all cursor-pointer"
              :class="visibleSegments[sIdx] ? 'active-chip' : 'inactive-chip'"
              @click="toggleSegment(sIdx)"
            >
              <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: segmentColors[sIdx] }" />
              <span>AHA {{ sIdx + 1 }}</span>
            </h6>
          </div> -->

          <div class="flex justify-end px-2 -mt-2">
            <span class="text-[9px] text-slate-400/80 italic select-none">
              Click to select phase · Drag to zoom · Double-click to reset
            </span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slice-indicator-badge {
  width: fit-content;
  align-items: center;
  gap: 4px;
  padding: 1.5px 7px;
  background: var(--md-surface-variant);
  color: var(--md-on-surface);
  border-radius: 12px;
  font-size: 10px;
  transition: all 0.2s ease;
}

.phase-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.01em;
}

.phase-toggle-btn:hover {
  transform: translateY(-1px);
}

.phase-toggle-btn:active {
  transform: translateY(0) scale(0.98);
}

.compute-curves-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  color: var(--md-primary);
  background: var(--md-surface-variant);
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.01em;
}

.compute-curves-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.compute-curves-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}

.compute-curves-btn.is-loading,
.compute-curves-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: none;
  transform: none;
}

.progress-fill {
  background: linear-gradient(90deg, var(--md-secondary), var(--md-primary));
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
}

.active-chip {
  color: #f8fafc;
}

.inactive-chip {
  color: #64748b;
  opacity: 0.6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.toggle-group {
  display: flex;
  width: fit-content;
  background: var(--glass-bg-secondary, rgba(15, 23, 42, 0.6));
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
  border-radius: 20px;
  padding: 2px;
  box-shadow: inset 0 1px 3px var(--glass-shadow, rgba(0, 0, 0, 0.3));
  flex-shrink: 0;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: var(--md-text-secondary, #94a3b8);
  padding: 0px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  text-transform: capitalize;
}

.toggle-btn:hover {
  color: var(--md-text-primary, #ffffff);
  background: var(--glass-bg-tertiary, rgba(255, 255, 255, 0.08));
}

.toggle-btn.active {
  background: var(--md-primary, #2563eb);
  color: var(--md-on-primary, #ffffff);
  box-shadow: 0 2px 6px var(--glass-shadow, rgba(0, 0, 0, 0.3));
  font-weight: 600;
}
</style>
